/** Formatação e classificação de frescura. Sem JSX, para poder ser testado. */

import { formatador } from "./tempo";
import type { NodeState } from "../types";

export type Frescura = "boa" | "aviso" | "critica";

/** Limiares de silêncio, em minutos. */
const AVISO_MIN = 3;
const CRITICA_MIN = 15;

/**
 * Classifica uma boia pelo tempo desde o último contacto.
 *
 * Usa `last_contact`, que é o relógio do **servidor**. O relógio do nó vem do
 * GNSS e deriva; se estiver errado, mente sobre o silêncio dele próprio.
 *
 * O watchdog dispara por ausência sustentada, nunca por um lote falhado — a 5 s
 * de cadência, perder um lote é comportamento normal do 4G.
 */
export function frescura(no: NodeState, agora: number = Date.now()): Frescura {
  if (no.last_contact === null) return "critica";
  const minutos = (agora - Date.parse(no.last_contact)) / 60_000;
  if (minutos >= CRITICA_MIN) return "critica";
  if (minutos >= AVISO_MIN) return "aviso";
  return "boa";
}

export const ROTULO_FRESCURA: Record<Frescura, string> = {
  boa: "a reportar",
  aviso: "atrasada",
  critica: "sem contacto",
};

/** "há 2 min", "há 3 h". Curto, porque vai dentro de células estreitas. */
export function haQuantoTempo(iso: string | null, agora: number = Date.now()): string {
  if (iso === null) return "sem dados";
  const s = Math.max(0, Math.round((agora - Date.parse(iso)) / 1000));
  if (s < 60) return `há ${s} s`;
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  return `há ${Math.floor(s / 86400)} d`;
}

// Todos os instantes saem no fuso fixo de `lib/tempo.ts`, nunca no do browser:
// por aqui passam os eixos, as dicas, as tabelas e as métricas do painel, e
// bastava um deles ficar de fora para voltar a haver duas horas no mesmo ecrã.

export function horaCurta(iso: string): string {
  return formatador({ hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export function horaComSegundos(iso: string): string {
  return formatador({ hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(
    new Date(iso),
  );
}

export function dataHora(iso: string): string {
  return formatador({
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(iso));
}

export function num(valor: number | null | undefined, casas = 2): string {
  return valor === null || valor === undefined ? "—" : valor.toFixed(casas);
}

export function gigabytes(bytes: number): string {
  return (bytes / 1_000_000_000).toFixed(1);
}

/** Uptime em "3 d 04 h" / "4 h 12 m". */
export function duracao(segundos: number): string {
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  if (d > 0) return `${d} d ${String(h).padStart(2, "0")} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} m`;
  return `${m} m`;
}
